<template>
  <div class="simple-chart" :style="{ height: height + 'px' }">
    <div ref="chartRef" class="chart-dom" />
  </div>
</template>

<script>
import { ref, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
import * as echarts from 'echarts'

const COLORS = ['#409EFF', '#67C23A', '#E6A23C', '#F56C6C', '#909399', '#9B59B6', '#00C9A7', '#845EC2']

function formatNum(n) {
  const v = Number(n) || 0
  if (v >= 10000) return `${(v / 10000).toFixed(2)}万`
  if (v >= 1000) return `${(v / 1000).toFixed(1)}k`
  return String(v)
}

export default {
  name: 'SimpleChart',
  props: {
    type: { type: String, default: 'line' }, // line | bar | pie
    labels: { type: Array, default: () => [] },
    series: { type: Array, default: () => [] }, // line/bar: [{ name, data, color }]; pie: [{ name, value, color }]
    height: { type: Number, default: 280 },
    theme: { type: String, default: 'light' }, // light | dark
  },
  setup(props) {
    const chartRef = ref(null)
    let chart = null
    let ro = null

    const buildOption = () => {
      const dark = props.theme === 'dark'
      const ink = dark ? 'rgba(247,241,234,0.78)' : '#606266'
      const mute = dark ? 'rgba(247,241,234,0.45)' : '#909399'
      const split = dark ? 'rgba(255,255,255,0.08)' : '#ebeef5'
      const axisLine = dark ? 'rgba(255,255,255,0.18)' : '#dcdfe6'
      const pieBorder = dark ? '#2a2220' : '#fff'

      if (props.type === 'pie') {
        const data = (props.series || []).map((s, i) => ({
          name: s.name || `项${i + 1}`,
          value: Number(s.value ?? s.data?.[0]) || 0,
          itemStyle: s.color ? { color: s.color } : { color: COLORS[i % COLORS.length] },
        }))
        // 创作中心画像/来源的 value 已是占比（合计约 100），勿再二次百分比误导
        const total = data.reduce((a, b) => a + b.value, 0)
        const looksLikePercent = total > 0 && total <= 105
        return {
          color: COLORS,
          tooltip: {
            trigger: 'item',
            formatter: (p) => {
              if (looksLikePercent) return `${p.name}<br/>${Number(p.value).toFixed(1)}%`
              const pct = total ? ((p.value / total) * 100).toFixed(1) : '0'
              return `${p.name}<br/>${formatNum(p.value)}（${pct}%）`
            },
          },
          legend: {
            orient: 'vertical',
            right: 8,
            top: 'center',
            type: 'scroll',
            textStyle: { fontSize: 12, color: ink },
          },
          series: [
            {
              type: 'pie',
              radius: ['42%', '68%'],
              center: ['38%', '50%'],
              avoidLabelOverlap: true,
              itemStyle: { borderRadius: 4, borderColor: pieBorder, borderWidth: 2 },
              label: {
                show: true,
                color: ink,
                formatter: (p) => {
                  if (looksLikePercent) return `${p.name}\n${Number(p.value).toFixed(0)}%`
                  const pct = total ? ((p.value / total) * 100).toFixed(0) : '0'
                  return `${p.name}\n${pct}%`
                },
                fontSize: 11,
              },
              emphasis: {
                label: { show: true, fontSize: 12, fontWeight: 'bold' },
                itemStyle: { shadowBlur: 10, shadowOffsetX: 0, shadowColor: 'rgba(0,0,0,0.15)' },
              },
              data,
            },
          ],
        }
      }

      const isBar = props.type === 'bar'
      const xData = props.labels || []
      const rawSeries = props.series || []
      const useDualAxis = rawSeries.some((s) => s.yAxisIndex === 1)

      const echartsSeries = rawSeries.map((s, i) => {
        const chartType = s.chartType || (isBar ? 'bar' : 'line')
        const isLine = chartType === 'line'
        const lineStyle = { ...(s.lineStyle || {}) }
        if (s.color && isLine) {
          lineStyle.color = s.color
        }
        return {
          name: s.name || `系列${i + 1}`,
          type: chartType,
          data: s.data || [],
          yAxisIndex: s.yAxisIndex || 0,
          smooth: isLine,
          symbol: 'circle',
          symbolSize: isLine ? 5 : undefined,
          showSymbol: isLine,
          emphasis: { focus: 'series' },
          itemStyle: s.color ? { color: s.color } : undefined,
          lineStyle: isLine ? lineStyle : undefined,
          areaStyle: isLine && s.area
            ? {
                color: {
                  type: 'linear',
                  x: 0, y: 0, x2: 0, y2: 1,
                  colorStops: [
                    { offset: 0, color: (s.color || COLORS[i % COLORS.length]) + '3d' },
                    { offset: 1, color: (s.color || COLORS[i % COLORS.length]) + '00' },
                  ],
                },
              }
            : undefined,
          barMaxWidth: chartType === 'bar' ? 22 : undefined,
        }
      })

      const yAxisBase = {
        type: 'value',
        axisLabel: {
          fontSize: 11,
          color: mute,
          formatter: (v) => formatNum(v),
        },
        splitLine: { lineStyle: { color: split } },
      }

      const yAxis = useDualAxis
        ? [
            { ...yAxisBase, name: '总量', nameTextStyle: { fontSize: 11, color: mute } },
            {
              ...yAxisBase,
              name: '篇数 / 篇均',
              position: 'right',
              nameTextStyle: { fontSize: 11, color: mute },
              splitLine: { show: false },
            },
          ]
        : [yAxisBase]

      return {
        color: COLORS,
        grid: {
          left: 52,
          right: useDualAxis ? 56 : 20,
          top: 48,
          bottom: 44,
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: isBar ? 'shadow' : 'cross' },
          formatter: (params) => {
            if (!params?.length) return ''
            const title = params[0].axisValueLabel || params[0].name
            const lines = params.map(
              (p) => `${p.marker}${p.seriesName}：<b>${formatNum(p.value)}</b>`,
            )
            return `${title}<br/>${lines.join('<br/>')}`
          },
        },
        legend: {
          type: 'scroll',
          top: 4,
          left: 8,
          right: useDualAxis ? 8 : 8,
          textStyle: { fontSize: 11, color: ink },
        },
        xAxis: {
          type: 'category',
          data: xData,
          axisLabel: {
            fontSize: 11,
            color: ink,
            rotate: xData.length > 6 ? 28 : 0,
            formatter: (v) => (String(v).length > 12 ? String(v).slice(0, 10) + '…' : v),
          },
          axisLine: { lineStyle: { color: axisLine } },
        },
        yAxis,
        series: echartsSeries,
      }
    }

    const render = () => {
      if (!chartRef.value) return
      if (!chart) chart = echarts.init(chartRef.value)
      const hasData =
        props.type === 'pie'
          ? (props.series || []).some((s) => Number(s.value ?? s.data?.[0]) > 0)
          : (props.labels || []).length > 0
      if (!hasData) {
        chart.clear()
        return
      }
      chart.setOption(buildOption(), { notMerge: true })
    }

    const scheduleRender = () => nextTick(render)

    const onResize = () => chart?.resize()

    onMounted(() => {
      scheduleRender()
      ro = new ResizeObserver(onResize)
      if (chartRef.value) ro.observe(chartRef.value)
    })

    onBeforeUnmount(() => {
      ro?.disconnect()
      chart?.dispose()
      chart = null
    })

    watch(
      () => [props.labels, props.series, props.type, props.height, props.theme],
      scheduleRender,
      { deep: true },
    )

    watch(
      () => props.height,
      () => nextTick(() => chart?.resize()),
    )

    return { chartRef }
  },
}
</script>

<style scoped>
.simple-chart {
  width: 100%;
}
.chart-dom {
  width: 100%;
  height: 100%;
}
</style>
